import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const MONTH_NAMES = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const monthLabel = (ym) => {
  const [y, m] = (ym || "").split("-");
  return `${MONTH_NAMES[parseInt(m) - 1]} ${y}`;
};

/** MonthlyReportsPane — listă rapoarte lunare + acțiuni edit/print pentru pontaj.
 * Extracted from PersonDetail.jsx (was lines 2853-2969).
 */
export default function MonthlyReportsPane({ personId, personName }) {
  const [list, setList] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  const [printMonth, setPrintMonth] = useState(null);

  const reload = () => {
    api.get(`/persons/${personId}/monthly-reports`)
      .then((r) => setList(r.data))
      .catch(() => setList({ reports: [] }));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [personId]);

  if (!list) return <div className="text-sm text-zinc-400 p-6">Se încarcă…</div>;
  if (list.no_employment_date) {
    return <div className="card-flat p-6 bg-amber-50 border-amber-300 text-amber-900 text-sm">⚠️ Această persoană nu are setată data angajării. Adaug-o în tabul „Date" pentru a putea genera rapoarte lunare.</div>;
  }
  const reports = list.reports || [];
  const finalized = reports.filter((r) => r.finalized).length;
  const generated = reports.filter((r) => r.exists && !r.finalized).length;
  const missing = reports.filter((r) => !r.exists).length;

  return (
    <div className="space-y-4">
      <div className="card-flat p-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="monthly-reports-summary">
        <div className="text-center">
          <div className="text-2xl font-display font-extrabold text-emerald-600">{finalized}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Finalizate</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-display font-extrabold text-amber-600">{generated}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Generate (nu finalizate)</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-display font-extrabold text-red-600">{missing}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Lipsă (vor fi auto-generate la deschidere)</div>
        </div>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left p-2 font-semibold text-zinc-700">Lună</th>
              <th className="text-right p-2 font-semibold text-zinc-700">Ore reale</th>
              <th className="text-right p-2 font-semibold text-zinc-700">Ore raport</th>
              <th className="text-right p-2 font-semibold text-zinc-700">Sumă raport</th>
              <th className="text-center p-2 font-semibold text-zinc-700">Status</th>
              <th className="text-center p-2 font-semibold text-zinc-700">Acțiune</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.month} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`monthly-row-${r.month}`}>
                <td className="p-2 font-semibold">{monthLabel(r.month)}</td>
                <td className="p-2 text-right tabular-nums text-zinc-600">{r.real_total_hours != null ? `${r.real_total_hours.toFixed(1)} h` : "—"}</td>
                <td className={`p-2 text-right tabular-nums font-semibold ${r.audit_total_hours !== r.real_total_hours ? "text-amber-700" : ""}`}>{r.audit_total_hours != null ? `${r.audit_total_hours.toFixed(1)} h` : "—"}</td>
                <td className="p-2 text-right tabular-nums">{r.audit_total_money != null ? `${r.audit_total_money.toFixed(2)} CHF` : "—"}</td>
                <td className="p-2 text-center">
                  {r.finalized ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">✅ Finalizat</span>
                  ) : r.exists ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">🟡 Generat</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-800 animate-pulse">❌ Lipsă</span>
                  )}
                </td>
                <td className="p-2 text-center">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setOpenMonth(r.month)} data-testid={`open-month-${r.month}`}>
                      {r.exists ? "Modifică" : "Generează & editează"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPrintMonth(r.month)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid={`print-month-${r.month}`}
                      title="Foaie A4 — listare pe zile"
                    >
                      🖨 A4
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openMonth && (
        <MonthlyReportEditDialog
          personId={personId}
          personName={personName}
          month={openMonth}
          onClose={() => setOpenMonth(null)}
          onSaved={() => { setOpenMonth(null); reload(); }}
        />
      )}

      {printMonth && (
        <MonthlyReportPrintDialog
          personId={personId}
          personName={personName}
          month={printMonth}
          onClose={() => setPrintMonth(null)}
        />
      )}
    </div>
  );
}


/** Construiește scheletul de zile pentru raportul AUDIT (al 2-lea).
 * Pornește de la `baseDays` (raportul efectiv) și suprascrie hours/project_label/note cu valorile audit2 dacă există.
 */
function buildAudit2Days(baseDays, audit2Rows) {
  const m = new Map((audit2Rows || []).map((r) => [r.date, r]));
  return baseDays.map((d) => {
    const a = m.get(d.date) || {};
    return {
      ...d,
      hours: a.hours != null ? a.hours : d.hours,
      hours_driven: a.hours_driven != null ? a.hours_driven : (d.hours_driven || ""),
      project_label: a.project_label != null ? a.project_label : d.project_label,
      note: a.note != null ? a.note : (d.note || ""),
    };
  });
}


/** Dialog edit raport lunar (editor zi-cu-zi). */
function MonthlyReportEditDialog({ personId, personName, month, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState([]);
  const [auditNote, setAuditNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState("ConVic GmbH");
  const [projects, setProjects] = useState([]);
  // Fișa de salariu (file_id stocat pe raport)
  const [slipFileId, setSlipFileId] = useState(null);
  const [slipMeta, setSlipMeta] = useState(null); // {original_filename, content_type, size}
  const [slipUploading, setSlipUploading] = useState(false);
  // Totaluri din fișa de salariu (completate manual pentru verificare)
  const [slipTotalHours, setSlipTotalHours] = useState("");
  const [slipTotalHoursDriven, setSlipTotalHoursDriven] = useState("");
  const slipInputRef = React.useRef(null);
  const [slipBlobUrl, setSlipBlobUrl] = useState(null);
  const [slipIsPdf, setSlipIsPdf] = useState(false);
  // RAPORT AUDIT (al 2-lea, ajustat la fișa salariu) — vizualizat în coloana dreaptă printr-un toggle
  const [rightPanel, setRightPanel] = useState("slip"); // "slip" | "audit"
  const [audit2Days, setAudit2Days] = useState([]); // copie editabilă a zilelor pentru raportul audit
  const [audit2Note, setAudit2Note] = useState("");
  useEffect(() => {
    if (!slipFileId) {
      setSlipBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    const token = sessionStorage.getItem("convic_token") || localStorage.getItem("convic_token") || "";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/files/${slipFileId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : Promise.reject(r.status))
      .then((b) => {
        if (cancelled) return;
        setSlipIsPdf((b.type || "").includes("pdf") || /\.pdf$/i.test(slipMeta?.original_filename || ""));
        setSlipBlobUrl(URL.createObjectURL(b));
      })
      .catch(() => { if (!cancelled) setSlipBlobUrl(null); });
    return () => { cancelled = true; };
  }, [slipFileId, slipMeta]);
  useEffect(() => () => { if (slipBlobUrl) URL.revokeObjectURL(slipBlobUrl); }, [slipBlobUrl]);

  const buildDays = (resp) => {
    const [y, m] = month.split("-").map((x) => parseInt(x, 10));
    const lastDay = new Date(y, m, 0).getDate();
    const audit = new Map((resp.daily_audit_rows || []).map((r) => [r.date, r]));
    const real = new Map((resp.real_daily_rows || []).map((r) => [r.date, r]));
    const RO_DOW = ["Du", "Lu", "Ma", "Mi", "Jo", "Vi", "Sâ"];
    const out = [];
    for (let d = 1; d <= lastDay; d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dow = new Date(y, m - 1, d).getDay();
      const isWeekend = dow === 0 || dow === 6;
      const r = audit.get(iso) || real.get(iso) || {};
      out.push({
        date: iso, day: d, dow: RO_DOW[dow], isWeekend,
        project_label: r.project_label || "",
        project_id: r.project_id || null,
        hours: r.hours ?? "",
        hours_driven: r.hours_driven ?? "",
        note: r.note || "",
        _hasReal: real.has(iso),
        _isAudit: audit.has(iso),
      });
    }
    return out;
  };

  useEffect(() => {
    setWeekendConfirmed(new Set());
    api.get(`/persons/${personId}/monthly-reports/${month}`)
      .then((r) => {
        setData(r.data);
        const built = buildDays(r.data);
        setDays(built);
        // Pre-marchează zilele de weekend cu conținut existent ca „deja confirmate"
        // ca să nu mai întrebe la prima editare a unui rând deja completat.
        const pre = new Set();
        built.forEach((d, i) => {
          if (d.isWeekend && ((parseFloat(d.hours) || 0) > 0 || (parseFloat(d.hours_driven) || 0) > 0 || (d.project_label || "").trim() || (d.note || "").trim())) {
            pre.add(i);
          }
        });
        if (pre.size > 0) setWeekendConfirmed(pre);
        setAuditNote(r.data.audit_note || "");
        // Inițializează raportul AUDIT (al 2-lea) — folosește audit2_rows dacă există, altfel clonează din raportul efectiv
        const a2Rows = (r.data.audit2_rows && r.data.audit2_rows.length > 0)
          ? r.data.audit2_rows
          : (r.data.daily_audit_rows || []).map((x) => ({ ...x }));
        setAudit2Days(buildAudit2Days(built, a2Rows));
        setAudit2Note(r.data.audit2_note || "");
        // Încarcă fișa de salariu deja salvată
        const sid = r.data.salary_slip_file_id || null;
        setSlipFileId(sid);
        setSlipTotalHours(r.data.slip_total_hours != null ? String(r.data.slip_total_hours) : "");
        setSlipTotalHoursDriven(r.data.slip_total_hours_driven != null ? String(r.data.slip_total_hours_driven) : "");
        if (sid) {
          api.get(`/files/${sid}/meta`).then((m) => setSlipMeta(m.data)).catch(() => setSlipMeta(null));
        } else {
          setSlipMeta(null);
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Eroare"));
    api.get(`/companies/employer-options`).then((r) => {
      const first = (r.data || []).find((c) => (c.name || "").toLowerCase().startsWith("convic"));
      if (first?.name) setCompanyName(first.name);
    }).catch(() => {});
    api.get(`/projects`).then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
    // eslint-disable-next-line
  }, [personId, month]);

  const MONTH_NAMES_RO = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie", "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"];
  const formatMonthRO = (ym) => {
    if (!ym) return "?";
    const [yy, mm] = ym.split("-");
    return `${MONTH_NAMES_RO[parseInt(mm, 10) - 1] || mm} ${yy}`;
  };

  const uploadSlip = async (file) => {
    if (!file) return;
    setSlipUploading(true);
    try {
      // ⚠️ Pas 1: OCR rapid — verifică lună + nume + total ore
      try {
        const detectForm = new FormData();
        detectForm.append("file", file);
        const dr = await api.post(
          `/persons/${personId}/monthly-reports/${month}/detect-slip-month`,
          detectForm,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        const det = dr.data || {};

        // 1a) Lună greșită?
        if (det.detected_month && !det.matches_expected) {
          const detLabel = formatMonthRO(det.detected_month);
          const expLabel = formatMonthRO(month);
          const ok = window.confirm(
            `⚠️ Atenție — vezi că e fișa de salariu greșită!\n\n` +
            `• Fișa încărcată: ${detLabel}\n` +
            `• Raport luna curentă: ${expLabel}\n\n` +
            `Vrei să o încarci totuși?`,
          );
          if (!ok) {
            toast.error(`Anulat — fișa pare să fie pentru ${detLabel}, nu pentru ${expLabel}`);
            setSlipUploading(false);
            return;
          }
        }

        // 1b) Nume greșit? (alt angajat)
        if (det.detected_employee_name && det.employee_name_matches === false) {
          const ok = window.confirm(
            `⚠️ Atenție — fișa pare să fie a altui angajat!\n\n` +
            `• Pe fișă: ${det.detected_employee_name}\n` +
            `• Angajat selectat: ${det.expected_employee_name || "—"}\n\n` +
            `Vrei să o încarci totuși?`,
          );
          if (!ok) {
            toast.error(`Anulat — fișa pare să fie pentru ${det.detected_employee_name}, nu pentru ${det.expected_employee_name}`);
            setSlipUploading(false);
            return;
          }
        }

        // 1c) Pre-populează slip_total_hours dacă l-a detectat OCR-ul
        if (det.detected_total_hours != null && !Number.isNaN(det.detected_total_hours)) {
          setSlipTotalHours(String(det.detected_total_hours));
          // Compară cu suma raportului
          const reportH = days.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0);
          const diff = Math.abs(det.detected_total_hours - reportH);
          if (diff > 0.25) {
            toast.warning(
              `⚠️ Diferență ${diff.toFixed(2)}h între raport (${reportH.toFixed(1)}h) și fișa de salariu (${det.detected_total_hours.toFixed(1)}h) — raportul NU va putea fi finalizat până nu se aliniază.`,
              { duration: 8000 },
            );
          }
        }
      } catch (detErr) {
        // OCR poate eșua (PDF mare, fișier corupt) — nu blocăm upload-ul.
        console.warn("Slip OCR detection failed:", detErr?.response?.data?.detail || detErr?.message);
      }

      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      const fid = r.data?.id;
      // Salvează imediat pe raport
      await api.put(`/persons/${personId}/monthly-reports/${month}`, {
        daily_audit_rows: days
          .filter((d) => (parseFloat(d.hours) > 0) || (parseFloat(d.hours_driven) > 0) || (d.project_label && d.project_label.trim()) || (d.note && d.note.trim()))
          .map((d) => ({
            date: d.date,
            project_id: d.project_id || null,
            project_label: d.project_label || null,
            hours: parseFloat(d.hours) || 0,
            hours_driven: parseFloat(d.hours_driven) || 0,
            note: d.note || null,
          })),
        audit_note: auditNote,
        salary_slip_file_id: fid,
      });
      setSlipFileId(fid);
      setSlipMeta(r.data);
      toast.success("✓ Fișa de salariu încărcată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la upload");
    } finally {
      setSlipUploading(false);
    }
  };

  const removeSlip = async () => {
    if (!window.confirm("Elimini fișa de salariu de pe acest raport?")) return;
    try {
      await api.put(`/persons/${personId}/monthly-reports/${month}`, {
        daily_audit_rows: days
          .filter((d) => (parseFloat(d.hours) > 0) || (parseFloat(d.hours_driven) > 0) || (d.project_label && d.project_label.trim()) || (d.note && d.note.trim()))
          .map((d) => ({
            date: d.date,
            project_id: d.project_id || null,
            project_label: d.project_label || null,
            hours: parseFloat(d.hours) || 0,
            hours_driven: parseFloat(d.hours_driven) || 0,
            note: d.note || null,
          })),
        audit_note: auditNote,
        salary_slip_file_id: null,
      });
      setSlipFileId(null);
      setSlipMeta(null);
      toast.success("Fișa de salariu eliminată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la eliminare");
    }
  };

  // Set de zile weekend (idx) deja confirmate de utilizator în această sesiune.
  // Evită întrebarea repetată la fiecare apăsare de tastă.
  const [weekendConfirmed, setWeekendConfirmed] = useState(() => new Set());

  const setDay = (idx, patch) => {
    const target = days[idx];
    if (target?.isWeekend && !weekendConfirmed.has(idx)) {
      // Verifică doar dacă utilizatorul chiar introduce conținut (nu la ștergere)
      const touchesContent = (
        ("hours" in patch && String(patch.hours || "").trim() !== "" && parseFloat(patch.hours) > 0) ||
        ("hours_driven" in patch && String(patch.hours_driven || "").trim() !== "" && parseFloat(patch.hours_driven) > 0) ||
        ("project_label" in patch && (patch.project_label || "").trim() !== "") ||
        ("note" in patch && (patch.note || "").trim() !== "")
      );
      if (touchesContent) {
        const dow = new Date(target.date + "T00:00:00").getDay();
        const dayLabel = dow === 0 ? "duminica" : "sâmbătă";
        const ok = window.confirm(`Ești sigur că s-a lucrat ${dayLabel} (${target.date})?`);
        if (!ok) return;
        setWeekendConfirmed((prev) => {
          const next = new Set(prev);
          next.add(idx);
          return next;
        });
      }
    }
    setDays((prev) => prev.map((d, i) => i === idx ? { ...d, ...patch } : d));
  };

  const totals = useMemo(() => {
    const hh = days.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0);
    const ht = days.reduce((s, d) => s + (parseFloat(d.hours_driven) || 0), 0);
    return { hours: hh, hours_driven: ht, total: hh + ht };
  }, [days]);

  const resetFromReal = async () => {
    if (!data) return;
    if (!window.confirm("Resetezi orele la valorile REALE (din planificator)? Editările manuale se pierd.")) return;
    try {
      const r = await api.get(`/persons/${personId}/monthly-reports/${month}`);
      const fresh = { ...r.data, daily_audit_rows: [] };
      setData(fresh);
      setDays(buildDays(fresh));
      toast.success("Resetat la valorile reale (neapsalvat — apasă Salvează pentru a confirma)");
    } catch (e) { toast.error("Eroare la resetare"); }
  };

  // Verifică discrepanța între total ore raport vs fișa de salariu (>0.25h => incomplet)
  const hoursMismatch = useMemo(() => {
    const reportH = days.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0);
    const slipH = slipTotalHours === "" ? null : parseFloat(slipTotalHours);
    if (slipH == null || Number.isNaN(slipH)) return null;
    const diff = Math.abs(reportH - slipH);
    if (diff <= 0.25) return null;
    return { report: reportH, slip: slipH, diff };
  }, [days, slipTotalHours]);

  const save = async (finalize) => {
    if (finalize && hoursMismatch) {
      const ok = window.confirm(
        `⚠️ NU SE POATE FINALIZA — orele NU sunt aliniate!\n\n` +
        `• Total raport: ${hoursMismatch.report.toFixed(1)}h\n` +
        `• Fișa de salariu: ${hoursMismatch.slip.toFixed(1)}h\n` +
        `• Diferență: ${hoursMismatch.diff.toFixed(2)}h\n\n` +
        `Raportul va rămâne marcat ca INCOMPLET până când orele se vor potrivi.\n\n` +
        `Vrei să salvezi totuși (fără finalizare)?`,
      );
      if (!ok) return;
      finalize = false;  // forțează salvare fără finalizare
    }
    setSubmitting(true);
    try {
      const daily_audit_rows = days
        .filter((d) => (parseFloat(d.hours) > 0) || (parseFloat(d.hours_driven) > 0) || (d.project_label && d.project_label.trim()) || (d.note && d.note.trim()))
        .map((d) => ({
          date: d.date,
          project_id: d.project_id || null,
          project_label: d.project_label || null,
          hours: parseFloat(d.hours) || 0,
          hours_driven: parseFloat(d.hours_driven) || 0,
          note: d.note || null,
        }));
      // === RAPORT AUDIT (al 2-lea) — salvat ca audit2_rows ===
      const audit2_rows = (audit2Days || [])
        .filter((d) => (parseFloat(d.hours) > 0) || (parseFloat(d.hours_driven) > 0) || (d.project_label && d.project_label.trim()) || (d.note && d.note.trim()))
        .map((d) => ({
          date: d.date,
          project_id: d.project_id || null,
          project_label: d.project_label || null,
          hours: parseFloat(d.hours) || 0,
          hours_driven: parseFloat(d.hours_driven) || 0,
          note: d.note || null,
        }));
      await api.put(`/persons/${personId}/monthly-reports/${month}`, {
        daily_audit_rows, audit_note: auditNote, finalized: finalize,
        salary_slip_file_id: slipFileId,
        slip_total_hours: slipTotalHours === "" ? null : parseFloat(slipTotalHours),
        slip_total_hours_driven: slipTotalHoursDriven === "" ? null : parseFloat(slipTotalHoursDriven),
        audit2_rows,
        audit2_note: audit2Note,
      });
      toast.success(finalize ? "Raport finalizat ✅" : "Modificări salvate");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la salvare"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[1500px] max-h-[95vh] overflow-y-auto" data-testid="monthly-edit-dialog">
        <DialogHeader>
          <DialogTitle>📋 Raport lunar — {personName} · {monthLabel(month)}</DialogTitle>
          <DialogDescription className="text-xs">
            Completează manual orele pe zi pentru raportul lunar. Câmpurile cu ⓘ vin auto din planificator. Pe viitor, când angajații introduc singuri orele, completarea va fi automată.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="p-6 text-sm text-zinc-400">Se încarcă…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_540px] gap-4 mt-2">
            <div className="space-y-3 min-w-0">
            <div className="flex items-start justify-between mb-1 pb-2 border-b-2 border-zinc-800">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">{companyName}</div>
                <div className="text-xl font-extrabold mt-0.5">Raport lunar pontaj — {monthLabel(month)}</div>
              </div>
              <div className="text-right text-xs">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Angajat</div>
                <div className="text-base font-bold">{personName}</div>
              </div>
            </div>

            <div className="overflow-x-auto rounded border">
              <table
                className="w-full text-sm"
                onKeyDown={(e) => {
                  // Navigare cu săgețile între celulele tabelului zilelor.
                  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
                  const target = e.target;
                  if (!target?.dataset?.cellRow || !target?.dataset?.cellCol) return;
                  // Evită să interfereze cu Left/Right pe text cu cursor în mijloc
                  if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && target.type !== "number") {
                    const len = (target.value || "").length;
                    const atStart = target.selectionStart === 0;
                    const atEnd = target.selectionStart === len;
                    if (e.key === "ArrowLeft" && !atStart) return;
                    if (e.key === "ArrowRight" && !atEnd) return;
                  }
                  e.preventDefault();
                  let row = parseInt(target.dataset.cellRow, 10);
                  let col = parseInt(target.dataset.cellCol, 10);
                  if (e.key === "ArrowUp") row--;
                  if (e.key === "ArrowDown") row++;
                  if (e.key === "ArrowLeft") col--;
                  if (e.key === "ArrowRight") col++;
                  // Cap col [0..3], cap row [0..days.length-1]
                  if (col < 0) { col = 3; row--; }
                  if (col > 3) { col = 0; row++; }
                  if (row < 0 || row >= days.length) return;
                  const next = e.currentTarget.querySelector(`[data-cell-row="${row}"][data-cell-col="${col}"]`);
                  if (next) {
                    next.focus();
                    if (typeof next.select === "function") next.select();
                  }
                }}
              >
                <thead className="bg-zinc-50 border-b text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="p-2 text-left w-[60px]">Ziua</th>
                    <th className="p-2 text-left w-[50px]">Zi</th>
                    <th className="p-2 text-left">Proiect / Locație</th>
                    <th className="p-2 text-right w-[110px]">Ore lucru</th>
                    <th className="p-2 text-right w-[110px]">Ore transport</th>
                    <th className="p-2 text-left">Observații</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d, idx) => (
                    <tr
                      key={d.date}
                      className={`border-b border-zinc-100 ${d.isWeekend ? "bg-zinc-50/60" : ""} ${d._isAudit ? "ring-1 ring-emerald-200" : ""} ${weekendConfirmed.has(idx) ? "ring-1 ring-emerald-400 bg-emerald-50/40" : ""}`}
                      data-testid={`day-row-${d.date}`}
                    >
                      <td className="p-1.5 text-right font-mono tabular-nums text-xs">{d.day}</td>
                      <td className="p-1.5 text-[11px] font-bold text-zinc-600">
                        {d.dow}
                        {d.isWeekend && weekendConfirmed.has(idx) && (
                          <span className="ml-1 text-emerald-600" title="Confirmat manual că s-a lucrat în weekend" data-testid={`weekend-confirmed-${d.date}`}>✓</span>
                        )}
                      </td>
                      <td className="p-1.5">
                        <Input
                          list={`projects-list-${month}`}
                          value={d.project_label || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            const match = projects.find((p) => (p.name || "") === val);
                            setDay(idx, { project_label: val, project_id: match?.id || null });
                          }}
                          placeholder={d._hasReal ? "(ⓘ din planificator)" : "—"}
                          className="h-8 text-xs"
                          data-testid={`day-project-${d.date}`}
                          data-cell-row={idx}
                          data-cell-col={0}
                        />
                      </td>
                      <td className="p-1.5">
                        <Input
                          type="number" step="0.25" min="0"
                          value={d.hours}
                          onChange={(e) => setDay(idx, { hours: e.target.value })}
                          className="h-8 text-xs text-right font-mono"
                          data-testid={`day-hours-${d.date}`}
                          data-cell-row={idx}
                          data-cell-col={1}
                        />
                      </td>
                      <td className="p-1.5">
                        <Input
                          type="number" step="0.25" min="0"
                          value={d.hours_driven}
                          onChange={(e) => setDay(idx, { hours_driven: e.target.value })}
                          className="h-8 text-xs text-right font-mono"
                          data-testid={`day-transport-${d.date}`}
                          data-cell-row={idx}
                          data-cell-col={2}
                        />
                      </td>
                      <td className="p-1.5">
                        <Input
                          value={d.note}
                          onChange={(e) => setDay(idx, { note: e.target.value })}
                          placeholder="—"
                          className="h-8 text-xs"
                          data-testid={`day-note-${d.date}`}
                          data-cell-row={idx}
                          data-cell-col={3}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-amber-100 font-extrabold text-sm">
                    <td colSpan={3} className="p-2 text-right">TOTAL LUNĂ</td>
                    <td className="p-2 text-right tabular-nums">{totals.hours.toFixed(2)}</td>
                    <td className="p-2 text-right tabular-nums">{totals.hours_driven.toFixed(2)}</td>
                    <td className="p-2 text-right tabular-nums">Σ {totals.total.toFixed(2)} h</td>
                  </tr>
                </tbody>
              </table>
              <datalist id={`projects-list-${month}`}>
                {projects.map((p) => <option key={p.id} value={p.name || ""} />)}
              </datalist>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={resetFromReal} data-testid="reset-from-real">
                <RefreshCw size={14} className="mr-1.5"/> Refă din planificator
              </Button>
              <div className="text-[11px] text-zinc-500 italic">
                Rândurile cu cadru verde subțire au fost editate manual (override pe pontaj).
              </div>
            </div>

            <div>
              <Label className="text-xs text-zinc-500">Notă (apare la print A4)</Label>
              <Textarea rows={2} value={auditNote} onChange={(e) => setAuditNote(e.target.value)} placeholder="ex: Rotunjit la 160h pentru raportul lunar" className="mt-1" data-testid="audit-note"/>
            </div>

            {data.finalized && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                ✅ Raport finalizat la {String(data.finalized_at || "").slice(0, 10)} de către <strong>{data.finalized_by || "—"}</strong>
              </div>
            )}
            {hoursMismatch && (
              <div className="text-xs bg-red-50 border-l-4 border-red-500 rounded p-2 space-y-0.5 animate-pulse-slow" data-testid="hours-mismatch-warning">
                <div className="font-bold text-red-700">⚠️ RAPORT INCOMPLET / GREȘIT — orele NU corespund cu fișa de salariu</div>
                <div className="text-red-800">
                  • Total raport: <strong>{hoursMismatch.report.toFixed(1)}h</strong> · Fișa de salariu: <strong>{hoursMismatch.slip.toFixed(1)}h</strong> · Diferență: <strong>{hoursMismatch.diff.toFixed(2)}h</strong>
                </div>
                <div className="text-red-600 italic">Aliniază orele înainte de finalizare. Raportul rămâne NEFINALIZAT.</div>
              </div>
            )}
            </div>

            {/* COLUMN DREAPTA: Fișa de salariu SAU Raport audit (toggle) */}
            <div className="space-y-2 min-w-0" data-testid="salary-slip-panel">
              {/* Toggle 📷 Fișă | 📊 Raport audit */}
              <div className="flex items-center gap-1 mb-1 sticky top-0 bg-white z-20 pt-1">
                <button
                  type="button"
                  onClick={() => setRightPanel("slip")}
                  className={`px-3 py-1.5 text-xs font-bold rounded transition-colors ${rightPanel === "slip" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                  data-testid="right-panel-tab-slip"
                >
                  📷 Fișă salariu
                </button>
                <button
                  type="button"
                  onClick={() => setRightPanel("audit")}
                  className={`px-3 py-1.5 text-xs font-bold rounded transition-colors ${rightPanel === "audit" ? "bg-indigo-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                  data-testid="right-panel-tab-audit"
                >
                  📊 Raport audit
                </button>
              </div>
              {rightPanel === "audit" ? (
                <AuditReportSubPanel
                  days={audit2Days}
                  setDays={setAudit2Days}
                  effectiveDays={days}
                  slipTotalHours={slipTotalHours}
                  audit2Note={audit2Note}
                  setAudit2Note={setAudit2Note}
                />
              ) : (
              <>
              <div className="flex items-center justify-between gap-2 sticky top-0 bg-white z-10 pb-1 border-b">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-base">💰</span>
                  <div className="font-bold text-sm">Fișa de salariu</div>
                  {slipMeta?.original_filename && (
                    <span className="text-[10px] text-zinc-500 truncate" title={slipMeta.original_filename}>
                      ({slipMeta.original_filename})
                    </span>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <input
                    ref={slipInputRef}
                    type="file"
                    hidden
                    accept="image/*,application/pdf"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSlip(f); e.target.value = ""; }}
                    data-testid="slip-file-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => slipInputRef.current?.click()}
                    disabled={slipUploading}
                    className="h-7 text-xs"
                    data-testid="slip-upload-btn"
                  >
                    {slipUploading ? "..." : (slipFileId ? "🔄 Schimbă" : "📤 Încarcă")}
                  </Button>
                  {slipFileId && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={removeSlip}
                      className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="slip-remove-btn"
                    >
                      ✗
                    </Button>
                  )}
                </div>
              </div>
              {/* Verificare totaluri: ce e scris pe fișa de salariu vs totalul din raport */}
              {(() => {
                const reportH = days.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0);
                const reportHd = days.reduce((s, d) => s + (parseFloat(d.hours_driven) || 0), 0);
                const slH = slipTotalHours === "" ? null : parseFloat(slipTotalHours);
                const slHd = slipTotalHoursDriven === "" ? null : parseFloat(slipTotalHoursDriven);
                const diffH = slH != null ? +(slH - reportH).toFixed(2) : null;
                const diffHd = slHd != null ? +(slHd - reportHd).toFixed(2) : null;
                const mismatchH = diffH != null && Math.abs(diffH) > 0.25;
                const mismatchHd = diffHd != null && Math.abs(diffHd) > 0.25;
                return (
                  <div className="rounded border border-zinc-200 bg-zinc-50 p-2 space-y-2 text-xs" data-testid="slip-totals-card">
                    <div className="font-bold text-zinc-700 uppercase tracking-wider text-[10px]">
                      🔍 Verifică totalurile cu raportul
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-zinc-500">Ore lucru (din fișa)</Label>
                        <Input
                          type="number" step="0.25" min="0"
                          value={slipTotalHours}
                          onChange={(e) => setSlipTotalHours(e.target.value)}
                          placeholder={`raport: ${reportH.toFixed(2)}h`}
                          className={`h-8 text-xs ${mismatchH ? "border-red-500 ring-1 ring-red-300 bg-red-50" : ""}`}
                          data-testid="slip-total-hours"
                        />
                        <div className="text-[10px] mt-0.5 tabular-nums">
                          Raport: <strong>{reportH.toFixed(2)}h</strong>
                          {diffH != null && (
                            <span className={`ml-1 font-bold ${mismatchH ? "text-red-700" : "text-emerald-700"}`}>
                              {mismatchH ? `· diff ${diffH > 0 ? "+" : ""}${diffH}h` : "✓ OK"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] text-zinc-500">Ore transport (din fișa)</Label>
                        <Input
                          type="number" step="0.25" min="0"
                          value={slipTotalHoursDriven}
                          onChange={(e) => setSlipTotalHoursDriven(e.target.value)}
                          placeholder={`raport: ${reportHd.toFixed(2)}h`}
                          className={`h-8 text-xs ${mismatchHd ? "border-red-500 ring-1 ring-red-300 bg-red-50" : ""}`}
                          data-testid="slip-total-hours-driven"
                        />
                        <div className="text-[10px] mt-0.5 tabular-nums">
                          Raport: <strong>{reportHd.toFixed(2)}h</strong>
                          {diffHd != null && (
                            <span className={`ml-1 font-bold ${mismatchHd ? "text-red-700" : "text-emerald-700"}`}>
                              {mismatchHd ? `· diff ${diffHd > 0 ? "+" : ""}${diffHd}h` : "✓ OK"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {(mismatchH || mismatchHd) && (
                      <div className="rounded bg-red-100 border border-red-400 px-2 py-1.5 text-[11px] font-bold text-red-900 animate-pulse" data-testid="slip-mismatch-banner">
                        ⚠ Discrepanță detectată — fișa de salariu nu coincide cu totalul raportului. Verifică și corectează zilele.
                      </div>
                    )}
                  </div>
                );
              })()}
              {!slipFileId ? (
                <div
                  onClick={() => slipInputRef.current?.click()}
                  className="rounded-lg border-2 border-dashed border-red-500 bg-red-50 p-6 text-center cursor-pointer hover:bg-red-100 text-red-900 animate-pulse-red-soft"
                  data-testid="slip-empty-dropzone"
                >
                  <div className="text-sm font-extrabold uppercase tracking-wider">⚠ FIȘA DE SALARIU LIPSEȘTE</div>
                  <div className="text-xs font-semibold mt-1">Apasă aici pentru a încărca fișa</div>
                  <div className="text-[11px] text-red-700 mt-2">
                    Fișa de salariu este <strong>obligatorie</strong> — raportul nu poate fi finalizat fără ea.<br />
                    PDF / JPG / PNG. Sistemul va verifica automat dacă fișa e pentru luna corectă.
                  </div>
                </div>
              ) : !slipBlobUrl ? (
                <div className="rounded border bg-zinc-50 p-6 text-center text-xs text-zinc-500">Se încarcă preview…</div>
              ) : slipIsPdf ? (
                <iframe
                  src={slipBlobUrl}
                  title="Fișa de salariu"
                  className="w-full border rounded bg-white"
                  style={{ height: "calc(95vh - 220px)", minHeight: "500px" }}
                  data-testid="slip-preview-pdf"
                />
              ) : (
                <div className="border rounded bg-white overflow-auto" style={{ maxHeight: "calc(95vh - 220px)" }}>
                  <img
                    src={slipBlobUrl}
                    alt="Fișa de salariu"
                    className="w-full h-auto object-contain"
                    data-testid="slip-preview-image"
                  />
                </div>
              )}
              </>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Închide</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={submitting} data-testid="audit-save">
            💾 Salvează (draft)
          </Button>
          <Button
            onClick={() => {
              if (!slipFileId) {
                toast.error("Fișa de salariu este obligatorie pentru finalizare — încarcă fișa în panoul din dreapta înainte de a finaliza raportul");
                return;
              }
              if (hoursMismatch) {
                toast.error(`Orele NU se aliniază cu fișa (${hoursMismatch.diff.toFixed(2)}h diferență). Raportul nu poate fi finalizat.`);
                return;
              }
              save(true);
            }}
            disabled={submitting || !!hoursMismatch}
            className={hoursMismatch ? "bg-red-300 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"}
            data-testid="audit-finalize"
            title={!slipFileId ? "Încarcă fișa de salariu pentru a finaliza" : hoursMismatch ? `Diferență ${hoursMismatch.diff.toFixed(2)}h — aliniază orele` : ""}
          >
            ✅ {data?.finalized ? "Re-finalizează" : "Finalizează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Dialog print A4 (read-only + print/save PDF). */
function MonthlyReportPrintDialog({ personId, personName, month, onClose }) {
  const [data, setData] = useState(null);
  const [companyName, setCompanyName] = useState("ConVic GmbH");

  useEffect(() => {
    api.get(`/persons/${personId}/monthly-reports/${month}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Eroare la încărcare raport"));
    api.get(`/companies/employer-options`).then((r) => {
      const first = (r.data || []).find((c) => (c.name || "").toLowerCase().startsWith("convic"));
      if (first?.name) setCompanyName(first.name);
    }).catch(() => {});
  }, [personId, month]);

  const days = useMemo(() => {
    if (!data) return [];
    const [y, m] = month.split("-").map((x) => parseInt(x, 10));
    const lastDay = new Date(y, m, 0).getDate();
    const auditMap = new Map((data.daily_audit_rows || []).map((r) => [r.date, r]));
    const realMap = new Map((data.real_daily_rows || []).map((r) => [r.date, r]));
    const out = [];
    const RO_DOW = ["Du", "Lu", "Ma", "Mi", "Jo", "Vi", "Sâ"];
    for (let d = 1; d <= lastDay; d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dow = new Date(y, m - 1, d).getDay();
      const isWeekend = dow === 0 || dow === 6;
      const row = auditMap.get(iso) || realMap.get(iso);
      out.push({
        day: d, iso, dow: RO_DOW[dow], isWeekend,
        project: row?.project_label || "",
        hours: row?.hours || 0,
        hours_driven: row?.hours_driven || 0,
        total: ((row?.hours || 0) + (row?.hours_driven || 0)),
        note: row?.note || "",
      });
    }
    return out;
  }, [data, month]);

  const totals = useMemo(() => {
    const sumH = days.reduce((s, d) => s + d.hours, 0);
    const sumT = days.reduce((s, d) => s + d.hours_driven, 0);
    return { hours: sumH, hours_driven: sumT, total: sumH + sumT };
  }, [days]);

  const handlePrint = () => window.print();

  if (!data) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="p-4 text-sm text-zinc-500 italic">Se încarcă...</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto print-a4-dialog" data-testid="monthly-print-dialog">
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .print-area, .print-area * { visibility: visible; }
            .print-area { position: absolute; inset: 0; margin: 0; padding: 0; width: 100%; }
            .no-print { display: none !important; }
            @page { size: A4 portrait; margin: 10mm; }
          }
          .print-area { font-family: 'Inter', system-ui, sans-serif; color: #000; background: #fff; padding: 8mm 10mm; }
          .print-table { width: 100%; border-collapse: collapse; font-size: 10.5pt; }
          .print-table th, .print-table td { border: 1px solid #444; padding: 3px 6px; }
          .print-table th { background: #f5f5f5; font-weight: 700; text-align: left; }
          .print-table td.num, .print-table th.num { text-align: right; font-variant-numeric: tabular-nums; }
          .print-table tr.weekend td { background: #f9fafb; color: #6b7280; }
          .print-table tr.total td { background: #fef3c7; font-weight: 800; font-size: 11.5pt; }
          .signature-area { margin-top: 14mm; display: grid; grid-template-columns: 1fr 1fr; gap: 18mm; }
          .signature-box { border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: 10pt; }
        `}</style>
        <div className="flex items-center justify-between no-print mb-3 gap-2">
          <DialogTitle className="text-lg font-bold">🖨 Raport lunar A4 — {monthLabel(month)}</DialogTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Închide</Button>
            <Button size="sm" onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="print-btn">
              🖨 Tipărește / Salvează PDF
            </Button>
          </div>
        </div>

        <div className="print-area">
          <div className="flex items-start justify-between mb-3 pb-3 border-b-2 border-black">
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-600">{companyName}</div>
              <div className="text-2xl font-extrabold mt-0.5">Raport lunar pontaj — {monthLabel(month)}</div>
            </div>
            <div className="text-right text-xs">
              <div className="text-[10px] uppercase tracking-wider text-zinc-600">Angajat</div>
              <div className="text-lg font-bold">{personName}</div>
              <div className="text-[10px] text-zinc-600 mt-0.5">Generat: {new Date().toLocaleDateString("ro-RO")}</div>
            </div>
          </div>

          <table className="print-table">
            <thead>
              <tr>
                <th style={{ width: "8%" }}>Ziua</th>
                <th style={{ width: "6%" }}>Zi</th>
                <th>Proiect / Locație</th>
                <th className="num" style={{ width: "10%" }}>Ore lucru</th>
                <th className="num" style={{ width: "10%" }}>Ore transport</th>
                <th className="num" style={{ width: "10%" }}>Total ore</th>
                <th style={{ width: "20%" }}>Observații</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.iso} className={d.isWeekend ? "weekend" : ""}>
                  <td className="num">{d.day}</td>
                  <td>{d.dow}</td>
                  <td>{d.project}</td>
                  <td className="num">{d.hours > 0 ? d.hours.toFixed(2) : ""}</td>
                  <td className="num">{d.hours_driven > 0 ? d.hours_driven.toFixed(2) : ""}</td>
                  <td className="num">{d.total > 0 ? d.total.toFixed(2) : ""}</td>
                  <td style={{ fontSize: "9pt" }}>{d.note}</td>
                </tr>
              ))}
              <tr className="total">
                <td colSpan={3} style={{ textAlign: "right" }}>TOTAL LUNĂ</td>
                <td className="num">{totals.hours.toFixed(2)}</td>
                <td className="num">{totals.hours_driven.toFixed(2)}</td>
                <td className="num">{totals.total.toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          {data.audit_note && (
            <div className="mt-3 text-xs">
              <div className="font-bold uppercase tracking-wider text-zinc-700 mb-1">Notă:</div>
              <div className="border border-zinc-300 p-2 bg-zinc-50">{data.audit_note}</div>
            </div>
          )}

          <div className="signature-area">
            <div className="signature-box">
              <div className="font-bold text-[10pt]">Semnătură angajat</div>
              <div className="text-[9pt] text-zinc-600">{personName}</div>
            </div>
            <div className="signature-box">
              <div className="font-bold text-[10pt]">Semnătură administrator</div>
              <div className="text-[9pt] text-zinc-600">{companyName}</div>
            </div>
          </div>

          <div className="mt-4 text-[8pt] text-zinc-500 text-center border-t border-zinc-200 pt-2">
            Document generat automat din sistemul intern ConVic ERP. Datele reflectă pontajul efectiv înregistrat în Planificator pentru luna {monthLabel(month)}.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * 📊 Raport AUDIT — sub-panel afișat în coloana dreaptă (toggle cu fișa salariu).
 *
 * Conține:
 * - Un sumar cu totalul raportului efectiv vs totalul fișei salariu vs total audit + diferență.
 * - Tabel editabil zi-cu-zi (mirror al raportului efectiv), cu indicator weekend (sâmbătă/duminică) ușor de identificat.
 * - Buton „Reset la efectiv" — copiază back valorile din raportul efectiv (anulează ajustările).
 * - Buton „Zero weekenduri" — setează 0h la sâmbete + duminici (cel mai des caz: orele sunt mai mici în fișa salariu pentru că weekendul nu se plătește).
 * - Textarea pentru note de audit (motivul ajustărilor).
 */
function AuditReportSubPanel({ days, setDays, effectiveDays, slipTotalHours, audit2Note, setAudit2Note }) {
  const effectiveTotal = (effectiveDays || []).reduce((s, d) => s + (parseFloat(d.hours) || 0), 0);
  const auditTotal = (days || []).reduce((s, d) => s + (parseFloat(d.hours) || 0), 0);
  const slipNum = slipTotalHours !== "" && slipTotalHours != null ? parseFloat(slipTotalHours) : null;
  const diff = slipNum != null ? (auditTotal - slipNum) : null;

  const dayName = (iso) => {
    try {
      const dt = new Date(iso + "T00:00:00");
      return ["DUM", "LUN", "MAR", "MIE", "JOI", "VIN", "SÂM"][dt.getDay()];
    } catch { return ""; }
  };
  const isWeekend = (iso) => {
    try {
      const dt = new Date(iso + "T00:00:00");
      return dt.getDay() === 0 || dt.getDay() === 6;
    } catch { return false; }
  };

  const updateDay = (idx, field, value) => {
    const next = [...days];
    next[idx] = { ...next[idx], [field]: value };
    setDays(next);
  };

  const resetToEffective = () => {
    if (!window.confirm("Resetează toate orele la valorile din raportul efectiv?")) return;
    const m = new Map((effectiveDays || []).map((d) => [d.date, d]));
    setDays(days.map((d) => ({ ...d, hours: m.get(d.date)?.hours ?? 0, hours_driven: m.get(d.date)?.hours_driven ?? 0, project_label: m.get(d.date)?.project_label ?? d.project_label, note: m.get(d.date)?.note ?? d.note })));
  };

  const zeroWeekends = () => {
    setDays(days.map((d) => isWeekend(d.date) ? { ...d, hours: 0, hours_driven: 0 } : d));
  };

  return (
    <div className="space-y-2" data-testid="audit2-sub-panel">
      {/* Sumar */}
      <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-3 space-y-1.5">
        <div className="text-[10px] uppercase font-bold tracking-wider text-indigo-700">📊 Raport AUDIT (ajustat la fișa salariu)</div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[9px] uppercase font-bold text-zinc-500">Efectiv</div>
            <div className="font-display font-extrabold text-base tabular-nums">{effectiveTotal.toFixed(2)}h</div>
          </div>
          <div>
            <div className="text-[9px] uppercase font-bold text-zinc-500">Fișă salariu</div>
            <div className="font-display font-extrabold text-base tabular-nums text-zinc-900">{slipNum != null ? `${slipNum.toFixed(2)}h` : "—"}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase font-bold text-zinc-500">Audit</div>
            <div className={`font-display font-extrabold text-base tabular-nums ${diff != null ? (Math.abs(diff) < 0.01 ? "text-emerald-700" : "text-red-700") : ""}`}>{auditTotal.toFixed(2)}h</div>
          </div>
        </div>
        {diff != null && (
          <div className={`text-[10px] text-center font-bold ${Math.abs(diff) < 0.01 ? "text-emerald-700" : "text-red-700"}`} data-testid="audit2-diff-indicator">
            {Math.abs(diff) < 0.01
              ? "✓ Auditul corespunde cu fișa salariu"
              : `⚠ Diferență față de fișă: ${diff > 0 ? "+" : ""}${diff.toFixed(2)}h — ajustează zilele pentru a egala`}
          </div>
        )}
        <div className="flex flex-wrap gap-1 pt-1 border-t border-indigo-200">
          <button
            type="button"
            onClick={resetToEffective}
            className="px-2 py-1 text-[10px] font-bold rounded bg-white border border-zinc-300 hover:bg-zinc-100 text-zinc-700"
            data-testid="audit2-reset-btn"
          >
            ↺ Reset la efectiv
          </button>
          <button
            type="button"
            onClick={zeroWeekends}
            className="px-2 py-1 text-[10px] font-bold rounded bg-white border border-amber-300 hover:bg-amber-50 text-amber-800"
            data-testid="audit2-zero-weekends-btn"
          >
            🚫 Zero la Sâ/Du
          </button>
        </div>
      </div>

      {/* Tabel editabil */}
      <div className="rounded-lg border border-zinc-200 overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-zinc-50">
            <tr className="text-left text-[9px] uppercase tracking-wider text-zinc-500">
              <th className="px-2 py-1.5">Zi</th>
              <th className="px-2 py-1.5">Proiect</th>
              <th className="px-2 py-1.5 text-right">Ore</th>
            </tr>
          </thead>
          <tbody>
            {(days || []).map((d, idx) => {
              const we = isWeekend(d.date);
              const effHours = parseFloat((effectiveDays || [])[idx]?.hours) || 0;
              const auditH = parseFloat(d.hours) || 0;
              const diffDay = auditH - effHours;
              return (
                <tr key={d.date} className={`border-t border-zinc-100 ${we ? "bg-amber-50/40" : ""}`} data-testid={`audit2-row-${d.date}`}>
                  <td className="px-2 py-1 font-mono tabular-nums whitespace-nowrap">
                    {d.date.slice(8)} <span className={`text-[9px] font-bold ${we ? "text-amber-700" : "text-zinc-400"}`}>{dayName(d.date)}</span>
                  </td>
                  <td className="px-2 py-1 text-zinc-700 max-w-[120px] truncate" title={d.project_label || ""}>
                    {d.project_label || <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-1 py-1 text-right">
                    <input
                      type="number" min={0} max={24} step={0.25}
                      value={d.hours ?? ""}
                      onChange={(e) => updateDay(idx, "hours", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                      className="w-14 h-6 px-1 text-right tabular-nums border border-zinc-300 rounded text-xs"
                      data-testid={`audit2-hours-${d.date}`}
                    />
                    {diffDay !== 0 && (
                      <div className={`text-[9px] tabular-nums font-bold ${diffDay > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {diffDay > 0 ? "+" : ""}{diffDay.toFixed(2)}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Notă audit */}
      <div>
        <label className="text-[10px] uppercase font-bold tracking-wider text-zinc-500">Note audit (motiv ajustări)</label>
        <textarea
          rows={2}
          value={audit2Note}
          onChange={(e) => setAudit2Note(e.target.value)}
          placeholder="Ex: scoase weekendurile pentru că nu se plătesc / redus orele pe proiectul X cu 2h…"
          className="mt-0.5 w-full text-xs border border-zinc-300 rounded p-1.5 resize-none"
          data-testid="audit2-note"
        />
      </div>
    </div>
  );
}

